/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { BackButton } from '@/components/Buttons/BackButton';
import { ContentContainer } from '@/components/ContentContainer';
import { QueryWrapper } from '@/components/QueryWrapper';
import { useConfigRevisionByIdQuery } from '@/hooks/query/useConfigRevisionByIdQuery';
import { useNodeConfigByIdQuery } from '@/hooks/query/useNodeConfigByIdQuery';
import { NodeConfigurationRevisionSourceCode } from '@/pages/ConfigRevision/NodeConfigurationRevisionSourceCode';
import { Container } from '@/primitives/Container';
import { Flex } from '@/primitives/Flex';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

export type ConfigRevisionPageProps = {
  nodeConfigId: string;
  configRevisionNumber: number;
};

export default function ConfigRevisionPage({ nodeConfigId, configRevisionNumber }: ConfigRevisionPageProps) {
  const tenantId = useMgmtPlaneStateStore((state) => state.selectedTenantID);
  const nodeConfigQuery = useNodeConfigByIdQuery({ nodeConfigId });
  const configRevisionQuery = useConfigRevisionByIdQuery({
    nodeConfigId,
    configRevisionNumber,
  });

  const getNodeConfigurationPath = () => `/tenants/${tenantId}/node-configs/${nodeConfigId}`;

  return (
    <ContentContainer width="100%">
      <Flex vertical gap="md">
        <Container>
          <BackButton to={`/tenants/${tenantId}/node-configs/${nodeConfigId}`} />
        </Container>
        <QueryWrapper queryResult={nodeConfigQuery}>
          {(nodeConfigData) => (
            <QueryWrapper queryResult={configRevisionQuery}>
              {(configRevisionData) => (
                <NodeConfigurationRevisionSourceCode
                  nodeConfiguration={nodeConfigData.data}
                  nodeConfigRevision={configRevisionData.data}
                  getNodeConfigurationPath={getNodeConfigurationPath}
                />
              )}
            </QueryWrapper>
          )}
        </QueryWrapper>
      </Flex>
    </ContentContainer>
  );
}
